import { useEffect, useMemo, useState } from "react";

import useAppSelector from "hooks/store/useAppSelector";
import useBonusActivation from "hooks/useBonusActivation";
import useGlobalVariables from "hooks/useGlobalVariables";
import SessionStorageUtils from "utils/sessionStorage";

import {
	selectPlayerCurrencyCode,
	selectSessionId,
	selectSessionBonuses,
	selectSessionFirstBonus,
	selectHasSessionBonus, selectSessionPromotions
} from "store/slices/auth/selectors";
import { selectCurrentGameType } from "store/slices/game/selectors";
import { binaryToFlags } from "utils/binaryCalculations";
import isEmptyArray from "utils/typeChecks/isEmptyArray";

import BonusModalNotificationView from "../bonusModalNotification-view";
import getStorageBonusDefaultData from "../helpers/getStorageBonusDefaultData";
import { selectModalReadyToShow } from "store/slices/bonus/selectors.js";

const BonusModalNotificationContainer = () => {
	const [isVisible, setIsVisible] = useState(false);
	const { isMobile, isTablet } = useGlobalVariables();
	const sessionId = useAppSelector(selectSessionId);
	const hasBonus = useAppSelector(selectHasSessionBonus);
	const bonuses = useAppSelector(selectSessionBonuses);
	const firstBonus = useAppSelector(selectSessionFirstBonus);
	const currencyCode = useAppSelector(selectPlayerCurrencyCode);
	const currentGameType = useAppSelector(selectCurrentGameType);
	const promotions = useAppSelector(selectSessionPromotions);
	const modalReadyToShow = useAppSelector(selectModalReadyToShow);

	const { hasError, message, activateBonus, assignedGameTypes } = useBonusActivation({
		gameType: currentGameType,
		bonus: firstBonus
	});

	const newBonuses = useMemo(() => {
		return bonuses.filter((bonus) => bonus.isNewBonus);
	}, [bonuses]);

	const hasNewBonuses = !isEmptyArray(newBonuses);

	const isSingleType = useMemo(() => {
		let _isSingleType = null;

		if (!hasBonus || isEmptyArray(newBonuses)) {
			return _isSingleType;
		}

		const allowedBonuses = newBonuses.filter((bonus) => {
			const allowedGameTypes = binaryToFlags(assignedGameTypes, bonus.gameType);
			return Boolean(allowedGameTypes.length);
		});

		if (isEmptyArray(allowedBonuses)) {
			return _isSingleType;
		}

		_isSingleType = allowedBonuses.length === 1;

		return _isSingleType;
	}, [hasBonus, newBonuses, assignedGameTypes]);

	useEffect(() => {
		if (!sessionId || !hasNewBonuses || !modalReadyToShow) {
			setIsVisible(false);
			return;
		}

		const storageBonusData = SessionStorageUtils.get(`bonus_${sessionId}`);
		if (Boolean(storageBonusData) && storageBonusData.modalNotificationHasAlreadyShown) {
			setIsVisible(false);
			return;
		}
		const defaultData = getStorageBonusDefaultData();
		defaultData.modalNotificationHasAlreadyShown = true;
		SessionStorageUtils.set(`bonus_${sessionId}`, defaultData);
		setIsVisible(hasNewBonuses);
	}, [hasNewBonuses, sessionId, modalReadyToShow]);

	const handleOnOk = () => {
		setIsVisible(false);
	};

	const handleOnActivateBonuses = () => {
		setIsVisible(false);
		activateBonus();
	};

	return (
		<BonusModalNotificationView
			isVisible={isVisible}
			bonuses={newBonuses}
			currencyCode={currencyCode}
			isSingleType={isSingleType}
			isMobile={isMobile}
			isTablet={isTablet}
			assignedGameTypes={assignedGameTypes}
			handleOnOk={handleOnOk}
			handleOnActivateBonuses={handleOnActivateBonuses}
			disableBtnActivateBonuses={hasError}
			disableBtnActivateBonusesReason={message}
		/>
	);
};
export default BonusModalNotificationContainer;
